import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MediaMatcher } from '@angular/cdk/layout';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TransitionService } from '@uirouter/core';
import { AppRouterBootstrapService } from 'src/app/app-common/services/app-router-bootstrap.service';
import { CommonHeaderBootstrapService } from 'src/app/app-common/services/common-header-bootstrap.service';
import { HelpWidgetService } from 'src/app/app-common/services/help-widget.service';
import { RouteMetadataService } from 'src/app/app-common/services/route-metadata.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { UserstateBootstrapService } from 'src/app/auth/services/userstate-bootstrap.service';
import { LoadingService } from 'src/app/components/loading/loading.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { LicenseGuardService } from 'src/app/components/plans/license-guard.service';
import { RvshareAppDetectionService } from 'src/app/screen-sharing/services/rvshare-app-detection.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { LiveAnnouncerService } from '../../../accessibility/services/live-announcer/live-announcer.service';

export type SidenavState = 'expand' | 'collapse' | 'open' | 'closed';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('sidenavAnimation', [
      state('expand', style({ width: '261px' })),
      state('collapse', style({ width: '61px' })),
      state('open', style({ width: '261px' })),
      state('closed', style({ width: '61px' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
      transition('open => collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('sidenavToggleAnimation', [
      state('expand', style({ transform: 'rotate(0deg)' })),
      state('collapse', style({ transform: 'rotate(180deg)' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('hideToggleAnimation', [
      state('expand', style({ opacity: 1 })),
      state('collapse', style({ opacity: 1 })),
      state('open', style({ opacity: 0 })),
      state('closed', style({ opacity: 0 })),
      transition('expand <=> closed', animate('0.15s ease-in-out')),
      transition('collapse <=> closed', animate('0.15s ease-in-out')),
      transition('open => expand', animate('0.15s ease-in-out')),
      transition('open => collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('sidenavContentAnimation', [
      state('expand', style({ marginLeft: '261px' })),
      state('collapse', style({ marginLeft: '61px' })),
      state('open', style({ marginLeft: '0' })),
      state('closed', style({ marginLeft: '0' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
      transition('collapse <=> open', animate('0.15s ease-in-out')),
    ]),
  ],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  sidenavState: SidenavState = 'expand';
  toggleAnimationState: SidenavState = 'expand';
  prevDesktopState: 'expand' | 'collapse' = 'expand';
  isAnimating = false;
  currentRoute: string = '';
  companyMenuOpened = false;
  hideNavigation;
  private readonly destroyRef = inject(DestroyRef);
  announcement = toSignal(
    this.liveAnnouncerService
      .getAnnouncement()
      .pipe(takeUntilDestroyed(this.destroyRef)),
  );

  public readonly isMobile = signal(true);
  public readonly loadingService = inject(LoadingService);
  // Feeds mat-sidenav's fixedTopGap and --common-header-height (see
  // updateCommonHeaderHeight).
  public readonly bannerHeight = signal(0);
  public readonly headerHeight = signal(0);
  private readonly FORCE_COLLAPSED_SIDEBAR_ROUTES = [
    'apps.chat',
    'apps.editor.create',
    'apps.editor.workspace.artboard',
    'apps.editor.workspace.htmleditor',
  ];
  private readonly FORCE_HIDDEN_SIDEBAR_ROUTES = ['apps.editor.templates.edit'];

  private readonly _mobileQuery: MediaQueryList;
  private readonly _mobileQueryListener: () => void;

  @ViewChild('contentLayoutBody') contentLayoutBody: ElementRef<HTMLElement>;

  constructor(
    private media: MediaMatcher,
    private transitionService: TransitionService,
    private rvshareAppDetectionService: RvshareAppDetectionService,
    private routeMetadataService: RouteMetadataService,
    private userStateService: UserStateService,
    private trackerService: TrackerService,
    private localStorageHelperService: LocalStorageHelperService,
    private liveAnnouncerService: LiveAnnouncerService,
    private licenseGuardService: LicenseGuardService,
    private helpWidgetService: HelpWidgetService,
    private userstateBootstrapService: UserstateBootstrapService,
    private commonHeaderBootstrapService: CommonHeaderBootstrapService,
    private appRouterBootstrapService: AppRouterBootstrapService,
  ) {
    this.userstateBootstrapService.initialize();
    this.appRouterBootstrapService.initialize();
    this.commonHeaderBootstrapService.initialize();
    this.helpWidgetService.initialize();

    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      // Rvshare app is always mobile
      this.isMobile.set(true);
      this.sidenavState = 'closed';
    } else {
      // Otherwise, set up the mobile query listener
      this._mobileQuery = this.media.matchMedia('(max-width: 1023px)');
      this.isMobile.set(this._mobileQuery.matches);

      if (this._mobileQuery.matches) {
        this.sidenavState = 'closed';
      } else {
        const savedSidenavState =
          this.localStorageHelperService.get('sidenavState');
        if (
          savedSidenavState === 'expand' ||
          savedSidenavState === 'collapse'
        ) {
          this.sidenavState = savedSidenavState;
          this.toggleAnimationState = savedSidenavState;
          this.prevDesktopState = savedSidenavState;
        }
      }

      this._mobileQueryListener = () => {
        this.isMobile.set(this._mobileQuery.matches);
        if (this._mobileQuery.matches) {
          if (
            this.sidenavState === 'expand' ||
            this.sidenavState === 'collapse'
          ) {
            this.prevDesktopState = this.sidenavState;
          }
          this.sidenavState = this.companyMenuOpened ? 'open' : 'closed';
        } else {
          if (this.shouldForceHiddenSidebar(this.currentRoute)) {
            this.sidenavState = 'closed';
          } else if (this.shouldForceCollapsedSidebar(this.currentRoute)) {
            this.sidenavState = 'collapse';
          } else {
            this.sidenavState = this.prevDesktopState;
          }
        }
      };
      this._mobileQuery.addEventListener('change', this._mobileQueryListener);
    }
    // TODO: This is a temporary solution to initialize the license guard service.
    // It should be initialized in the app module instead.
    this.licenseGuardService.init();
  }

  ngOnInit() {
    this.transitionService.onSuccess({}, (transition) => {
      const toState = transition.to().name;
      const fromState = transition.from().name;
      this.hideNavigation =
        toState.indexOf('apps.auth') !== -1 ||
        toState.indexOf('apps.screen-sharing.home') !== -1 ||
        toState.indexOf('apps.billing.invoice') !== -1 ||
        toState.indexOf('apps.billing.unpaid') !== -1;
      if (this.hideNavigation || this.isMobile()) {
        this.sidenavState = 'closed';
      } else if (!this.isMobile()) {
        if (this.shouldForceHiddenSidebar(toState)) {
          this.sidenavState = 'closed';
        } else if (this.shouldForceCollapsedSidebar(toState)) {
          this.sidenavState = 'collapse';
        } else {
          if (
            this.shouldForceCollapsedSidebar(fromState) ||
            this.shouldForceHiddenSidebar(fromState)
          ) {
            const savedSidenavState =
              this.localStorageHelperService.get('sidenavState');
            if (
              savedSidenavState === 'expand' ||
              savedSidenavState === 'collapse'
            ) {
              this.sidenavState = savedSidenavState;
              this.prevDesktopState = savedSidenavState;
            } else {
              this.sidenavState = 'expand';
              this.prevDesktopState = 'expand';
            }
          } else {
            this.sidenavState = this.prevDesktopState;
          }
        }
      }
      this.currentRoute = toState;
      this.updateTitle();
      // hideNavigation removes the banner from the DOM without firing a
      // resize event, so explicitly recompute --common-header-height here
      // to avoid it keeping a stale banner height baked in.
      this.updateCommonHeaderHeight();
    });
  }

  ngOnDestroy() {
    this.commonHeaderBootstrapService.destroy();

    if (!this.rvshareAppDetectionService.isScreenShareApp()) {
      this._mobileQuery.removeEventListener(
        'change',
        this._mobileQueryListener,
      );
    }
  }

  toggleSidenav() {
    this.sidenavState = this.sidenavState === 'expand' ? 'collapse' : 'expand';
    this.prevDesktopState = this.sidenavState;

    if (
      !this.shouldForceCollapsedSidebar(this.currentRoute) &&
      !this.shouldForceHiddenSidebar(this.currentRoute)
    ) {
      this.localStorageHelperService.set('sidenavState', this.sidenavState);
    }

    this.trackerService.userEvent(
      'Toggle Sidebar',
      this.userStateService.getUsername(),
      true,
      {
        toState: this.sidenavState,
      },
    );
  }

  openMobileMenu() {
    if (this.isMobile()) {
      this.sidenavState = 'open';
    }
  }

  toggleHeaderNavigation() {
    if (this.isMobile()) {
      this.openMobileMenu();
      return;
    }

    if (this.shouldForceHiddenSidebar(this.currentRoute)) {
      this.sidenavState =
        this.sidenavState === 'closed' ? 'collapse' : 'closed';
      return;
    }

    this.toggleSidenav();
  }

  onOpenedChange(opened: boolean) {
    if (this.isMobile()) {
      this.sidenavState = opened ? 'open' : 'closed';
    }
  }

  onAnimationStart(event: any) {
    this.isAnimating = true;
    // Only trigger toggle animation for expand/collapse transitions
    if (event.toState === 'expand' || event.toState === 'collapse') {
      this.toggleAnimationState = event.toState;
    }
  }

  onAnimationDone(event: any) {
    this.isAnimating = false;
  }

  updateTitle(): void {
    const routeMetadata = this.routeMetadataService.getActiveRouteMetadata();
    const titleText = routeMetadata ? `${routeMetadata.title} | ` : '';

    document.title = titleText + 'Rise Vision Apps';
  }

  onHeaderResize(event: { width: number; height: number }) {
    this.headerHeight.set(Math.round(event.height));
    this.updateCommonHeaderHeight();
  }

  onBannerResize(event: { width: number; height: number }) {
    this.bannerHeight.set(Math.round(event.height));
    this.updateCommonHeaderHeight();
  }

  onSkipToContent(event: Event) {
    event.preventDefault();
    this.contentLayoutBody?.nativeElement?.focus();
  }

  // Keeps --common-header-height (consumed elsewhere via calc(100vh -
  // var(--common-header-height))) in sync with the header + banner height.
  private updateCommonHeaderHeight(): void {
    if (this.contentLayoutBody) {
      // hideNavigation removes the banner from the DOM, so its last
      // measured height would otherwise be stale here.
      const bannerHeight = this.hideNavigation ? 0 : this.bannerHeight();
      this.contentLayoutBody.nativeElement.style.setProperty(
        '--common-header-height',
        this.headerHeight() + bannerHeight + 'px',
      );
    }
  }

  private shouldForceCollapsedSidebar(routeName: string): boolean {
    return this.FORCE_COLLAPSED_SIDEBAR_ROUTES.includes(routeName);
  }

  private shouldForceHiddenSidebar(routeName: string): boolean {
    return this.FORCE_HIDDEN_SIDEBAR_ROUTES.includes(routeName);
  }
}
